import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Box, Checkbox, Typography, CircularProgress, Tooltip,
} from '@mui/material';
import PushPinIcon from '@mui/icons-material/PushPin';
import * as C from '../../../theme/colors.constants';
import { agruparHistoricosPorData, corTipoHistorico } from './historicoController';
import { tipoExibicaoHistorico } from '../../../../models/types';

const LIMIAR_SCROLL_PX = 30;
const TAMANHO_BOLINHA_PX = 16;

function isFimDoScroll(target) {
  const { scrollTop, clientHeight, scrollHeight } = target;
  return (scrollTop + clientHeight) >= (scrollHeight - LIMIAR_SCROLL_PX);
}

/**
 * Indicador "fixar" — bolinha colorida por `idTipoHistorico`, mesmo padrão
 * visual da tela real (`HistoricoProcesso.styles.jsx`: `.checkmark`, círculo
 * de 16px com `box-shadow` e cor de fundo por tipo). Reaproveita o
 * `Checkbox` do MUI (mesma técnica da tela real: `<input type="checkbox">`
 * nativo, visualmente oculto, por trás do elemento visível — aqui via
 * `icon`/`checkedIcon`) em vez de reinventar foco/teclado/a11y com uma div.
 *
 * 3 estados (idênticos à tela real):
 * - **Não fixado**: bolinha sólida na cor do tipo, sem ícone.
 * - **Fixado pelo usuário** (`FIXO`): bolinha sólida na cor do tipo, com o
 *   pin branco por cima (equivalente a `pinDefault.svg`, usado para todo
 *   tipo na tela real).
 * - **Fixado pelo sistema** (`FIXOSISTEMA`, sempre não-interativo): bolinha
 *   BRANCA com anel na cor do tipo e pin na cor do tipo (equivalente a
 *   `pinnedBackground`/`pinnedBorder`).
 *
 * Divergência deliberada da tela real: os 10 SVGs `pin*.svg` usados no
 * estado "fixado pelo sistema" são o MESMO glifo de pin recolorido por tipo
 * (confirmado pelo padrão de nomes — `pinGray`/`pinGreen`/`pinBlue`/etc.),
 * não 10 ícones distintos — por isso este port usa um único
 * `PushPinIcon` colorido dinamicamente via `sx`, em vez de embutir 10
 * arquivos SVG quase idênticos.
 */
function IndicadorFixarHistorico({
  idProcessoHistorico, idTipoHistorico, fixar, disabled, onChange,
}) {
  const cor = corTipoHistorico(idTipoHistorico);
  const fixadoPeloSistema = fixar === tipoExibicaoHistorico.fixoSistema;
  const marcado = fixar !== tipoExibicaoHistorico.naoFixo;

  const estiloBase = {
    width: TAMANHO_BOLINHA_PX,
    height: TAMANHO_BOLINHA_PX,
    borderRadius: '50%',
    boxShadow: '0px 0px 2px 1px rgba(0,0,0,0.48)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  return (
    <Tooltip title={fixadoPeloSistema ? 'Fixado automaticamente' : 'Fixar no topo'} arrow>
      <span>
        <Checkbox
          size="small"
          disableRipple
          checked={marcado}
          disabled={disabled || fixadoPeloSistema}
          onChange={e => onChange(idProcessoHistorico, e.target.checked)}
          sx={{ p: 0, mt: 0.25, alignSelf: 'flex-start' }}
          icon={<Box sx={{ ...estiloBase, backgroundColor: cor }} />}
          checkedIcon={(
            <Box
              sx={{
                ...estiloBase,
                backgroundColor: fixadoPeloSistema ? C.white : cor,
                border: fixadoPeloSistema ? `1px solid ${cor}` : 'none',
              }}
            >
              <PushPinIcon sx={{ fontSize: 10, color: fixadoPeloSistema ? cor : C.white }} />
            </Box>
          )}
        />
      </span>
    </Tooltip>
  );
}
IndicadorFixarHistorico.propTypes = {
  idProcessoHistorico: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  idTipoHistorico: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  fixar: PropTypes.string.isRequired,
  disabled: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
};
IndicadorFixarHistorico.defaultProps = { idTipoHistorico: null };

/**
 * Lista de Histórico (Ocorrências) — port de `HistoricoProcesso.jsx` real
 * para MUI v5. Scroll-infinito replicado fielmente (mesmo limiar de 30px,
 * mesmo guard `!historicos.last`); grupo por data simplificado (ver
 * `agruparHistoricosPorData`); "fixar" replicado com o mesmo endpoint real
 * (`updateTipoExibicaoHistorico`, wireado pelo container) e o mesmo padrão
 * visual de bolinha colorida (`IndicadorFixarHistorico`).
 *
 * **Texto do histórico nunca é interpretado como HTML** — divergência
 * deliberada da tela real (que usa `html-react-parser` sobre
 * `historico.texto`, funcionalmente equivalente a
 * `dangerouslySetInnerHTML`). Aqui, `historicoController.formatarPartesTexto`
 * só localiza o prefixo conhecido ("Simulação:"/"Acordo:") e devolve partes
 * de texto puro, renderizadas como `<Box component="span">` comum — sem
 * nenhum vetor de XSS novo introduzido por este port.
 */
export default function ListaHistorico({
  historicos, carregandoMais, desabilitarFixar, onScrollFim, onFixarHistorico, altura,
}) {
  const grupos = useMemo(() => agruparHistoricosPorData(historicos?.content || []), [historicos]);

  const onScroll = (e) => {
    if (historicos && !historicos.last && !carregandoMais && isFimDoScroll(e.currentTarget)) {
      onScrollFim();
    }
  };

  if (!historicos) {
    return (
      <Typography variant="body2" sx={{ color: C.warmGrey, textAlign: 'center', py: 4 }}>
        Carregando...
      </Typography>
    );
  }

  // Checa `grupos` (pós-agrupamento), não `historicos.content` — um item
  // sem `dataCadastramento` é descartado por `agruparHistoricosPorData`
  // (nunca entra em grupo nenhum); se TODOS os itens da página caírem
  // nesse caso, `content.length` seria > 0 mas `grupos` ficaria vazio, e
  // checar `content.length` deixaria a tela em branco sem nenhum feedback,
  // mascarando dado ausente como se estivesse tudo ok.
  if (grupos.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: C.warmGrey, textAlign: 'center', py: 4 }}>
        Nenhuma ocorrência encontrada.
      </Typography>
    );
  }

  return (
    <Box onScroll={onScroll} sx={{ maxHeight: altura, overflowY: 'auto' }}>
      {grupos.map(grupo => (
        <Box key={`${grupo.dataLabel}-${grupo.itens[0].idProcessoHistorico}`} sx={{ mb: 2 }}>
          <Typography sx={{
            fontSize: 12, fontWeight: 700, color: C.navy, textTransform: 'uppercase', mb: 1,
          }}
          >
            {grupo.dataLabel}
          </Typography>

          {grupo.itens.map((historico) => {
            const corTipo = corTipoHistorico(historico.idTipoHistorico);
            return (
              <Box
                key={historico.idProcessoHistorico}
                sx={{
                  display: 'flex', gap: 1, py: 1.25, borderBottom: `1px solid ${C.borderGrey}`,
                }}
              >
                <IndicadorFixarHistorico
                  idProcessoHistorico={historico.idProcessoHistorico}
                  idTipoHistorico={historico.idTipoHistorico}
                  fixar={historico.fixar}
                  disabled={desabilitarFixar}
                  onChange={onFixarHistorico}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2">
                    <Box component="span" sx={{ fontWeight: 700 }}>Data: </Box>
                    {historico.dataHora}
                  </Typography>
                  <Typography variant="body2">
                    <Box component="span" sx={{ fontWeight: 700 }}>Autor: </Box>
                    {historico.usuario}
                  </Typography>
                  {historico.partesTexto.length > 0 && (
                    <Typography variant="body2" component="div" sx={{ whiteSpace: 'pre-wrap' }}>
                      <Box component="span" sx={{ fontWeight: 700, color: corTipo }}>
                        {historico.descricaoTipo}
                        {': '}
                      </Box>
                      {historico.partesTexto.map(parte => (
                        <Box
                          key={parte.texto}
                          component="span"
                          sx={{ fontWeight: parte.negrito ? 700 : 400 }}
                        >
                          {parte.texto}
                        </Box>
                      ))}
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      ))}

      {carregandoMais && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={20} />
        </Box>
      )}
    </Box>
  );
}
ListaHistorico.propTypes = {
  historicos: PropTypes.shape({
    content: PropTypes.array,
    last: PropTypes.bool,
  }),
  carregandoMais: PropTypes.bool,
  desabilitarFixar: PropTypes.bool,
  onScrollFim: PropTypes.func.isRequired,
  onFixarHistorico: PropTypes.func.isRequired,
  altura: PropTypes.number,
};
ListaHistorico.defaultProps = {
  historicos: null,
  carregandoMais: false,
  desabilitarFixar: false,
  altura: 560,
};
